(() => {
  'use strict';
  const FIGHT_DURATION = 4400;
  const DEMO_DELAY = 800;
  const scenes = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let paused = false;
  try { paused = sessionStorage.getItem('cage-punch-motion') === 'paused'; } catch (_) {}

  function motionStopped() {
    return paused || reducedMotion.matches || document.hidden;
  }

  function syncMotion() {
    document.body.toggleAttribute('data-motion-paused', motionStopped());
    scenes.forEach((scene) => scene.sync());
  }

  function mount(root) {
    if (scenes.has(root)) return;
    const trigger = root.querySelector('[data-fight-trigger]');
    const toggle = root.querySelector('[data-motion-toggle]');
    const pageToggle = document.querySelector('[data-page-motion-toggle]');
    const pageMotionLabel = pageToggle?.querySelector('[data-page-motion-label]');
    const pageMotionIcon = pageToggle?.querySelector('[data-page-motion-icon]');
    const triggerLabel = root.querySelector('[data-trigger-label]');
    const motionLabel = root.querySelector('[data-motion-label]');
    const status = root.querySelector('[data-fight-status]');
    const labelsNode = root.querySelector('[data-fight-labels]');
    const stage = root.querySelector('[data-fight-stage]') || root;
    const poses = Array.from(root.querySelectorAll('[data-fight-pose]'));
    if (!trigger || !toggle || !triggerLabel || !motionLabel || !status || !labelsNode) return;
    let labels;
    try { labels = JSON.parse(labelsNode.textContent); } catch (_) { return; }
    root.dataset.state = 'ready';
    trigger.disabled = false;
    triggerLabel.textContent = labels.trigger;
    status.textContent = '';
    root.removeAttribute('data-auto-demo');
    root.removeAttribute('data-poses-ready');
    let timer = null;
    let demoTimer = null;
    let observer = null;
    let mounted = true;
    let inView = true;
    let demoVisible = false;
    const inEditor = Boolean(window.Shopify?.designMode || root.dataset.designMode === 'true');
    let demoUsed = inEditor;
    let posesSettled = poses.length === 0;
    let posesReady = false;
    const imageCleanups = new Set();

    function cancelDemo() {
      if (demoTimer !== null) window.clearTimeout(demoTimer);
      demoTimer = null;
    }

    function applyPoses() {
      // A manual fight can start while images load. Keep its artwork consistent.
      if (root.dataset.state !== 'fighting') root.toggleAttribute('data-poses-ready', posesReady);
    }

    function finish() {
      if (!mounted) return;
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      cancelDemo();
      root.dataset.state = 'finished';
      root.removeAttribute('data-auto-demo');
      trigger.disabled = false;
      triggerLabel.textContent = labels.replay;
      status.textContent = labels.finished;
      applyPoses();
    }

    function startFight(automatic) {
      if (root.dataset.state === 'fighting') return;
      cancelDemo();
      demoUsed = true;
      if (motionStopped() || !inView) {
        finish();
        return;
      }
      root.toggleAttribute('data-auto-demo', automatic);
      root.dataset.state = 'fighting';
      trigger.disabled = true;
      status.textContent = labels.fighting;
      timer = window.setTimeout(finish, FIGHT_DURATION);
    }

    function fight() {
      startFight(false);
    }

    function scheduleDemo() {
      if (!mounted || demoUsed || !demoVisible || !posesSettled || motionStopped() || root.dataset.state !== 'ready') {
        cancelDemo();
        return;
      }
      if (demoTimer !== null) return;
      demoTimer = window.setTimeout(() => {
        demoTimer = null;
        if (mounted && !demoUsed && demoVisible && !motionStopped()) startFight(true);
      }, DEMO_DELAY);
    }

    function decodePose(image) {
      if (typeof image.decode === 'function') return image.decode();
      if (image.complete) return image.naturalWidth ? Promise.resolve() : Promise.reject();
      return new Promise((resolve, reject) => {
        function cleanup() {
          image.removeEventListener('load', loaded);
          image.removeEventListener('error', failed);
          imageCleanups.delete(cleanup);
        }
        function loaded() { cleanup(); image.naturalWidth ? resolve() : reject(); }
        function failed() { cleanup(); reject(); }
        image.addEventListener('load', loaded);
        image.addEventListener('error', failed);
        imageCleanups.add(cleanup);
      });
    }

    function toggleMotion() {
      paused = !paused;
      try { sessionStorage.setItem('cage-punch-motion', paused ? 'paused' : 'playing'); } catch (_) {}
      syncMotion();
    }

    function sync() {
      const stopped = paused || reducedMotion.matches;
      toggle.setAttribute('aria-pressed', String(stopped));
      motionLabel.textContent = stopped ? labels.resume : labels.pause;
      toggle.hidden = reducedMotion.matches;
      if (pageToggle) {
        pageToggle.setAttribute('aria-pressed', String(stopped));
        pageToggle.hidden = reducedMotion.matches || inView || inEditor;
        if (pageMotionLabel) pageMotionLabel.textContent = stopped ? labels.resume : labels.pause;
        if (pageMotionIcon) pageMotionIcon.dataset.state = stopped ? 'paused' : 'playing';
      }
      if ((motionStopped() || !inView) && root.dataset.state === 'fighting') finish();
      scheduleDemo();
    }

    trigger.addEventListener('click', fight);
    toggle.addEventListener('click', toggleMotion);
    pageToggle?.addEventListener('click', toggleMotion);
    trigger.hidden = false;
    toggle.hidden = reducedMotion.matches;
    if ('IntersectionObserver' in window) {
      observer = new window.IntersectionObserver((entries) => {
        if (!mounted) return;
        entries.forEach((entry) => {
          if (entry.target && entry.target !== stage) return;
          const ratio = entry.intersectionRatio ?? (entry.isIntersecting ? 1 : 0);
          inView = entry.isIntersecting && ratio > 0;
          demoVisible = inView && ratio >= 0.6;
          root.toggleAttribute('data-offscreen', !inView);
          sync();
        });
      }, { threshold: [0, 0.6] });
      observer.observe(stage);
    }
    scenes.set(root, {
      sync,
      destroy() {
        mounted = false;
        if (timer !== null) window.clearTimeout(timer);
        cancelDemo();
        observer?.disconnect();
        imageCleanups.forEach((cleanup) => cleanup());
        trigger.removeEventListener('click', fight);
        toggle.removeEventListener('click', toggleMotion);
        pageToggle?.removeEventListener('click', toggleMotion);
        if (pageToggle) pageToggle.hidden = true;
        trigger.hidden = true;
        trigger.disabled = false;
        toggle.hidden = true;
        root.dataset.state = 'ready';
        root.removeAttribute('data-offscreen');
        root.removeAttribute('data-auto-demo');
        root.removeAttribute('data-poses-ready');
        scenes.delete(root);
      },
    });
    sync();
    if (poses.length) {
      Promise.allSettled(poses.map((image) => Promise.resolve().then(() => decodePose(image)))).then((results) => {
        if (!mounted) return;
        posesSettled = true;
        posesReady = poses.length === 2 && results.every((result) => result.status === 'fulfilled');
        applyPoses();
        scheduleDemo();
      });
    }
  }

  function mountWithin(container) {
    if (container.matches?.('[data-fight-scene]')) mount(container);
    container.querySelectorAll('[data-fight-scene]').forEach(mount);
    syncMotion();
  }

  mountWithin(document);
  reducedMotion.addEventListener('change', syncMotion);
  document.addEventListener('visibilitychange', syncMotion);
  document.addEventListener('shopify:section:load', (event) => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    scenes.forEach((scene, root) => {
      if (event.target === root || event.target.contains(root)) scene.destroy();
    });
  });
})();
